import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { QuoteCard } from "../quotes/QuotesComponents";
import { formatDate, isOverdue, getPaymentStructureLabel, copyToClipboard, calculateVATInclusiveAmount, sendEmail } from "../../lib/appUtils";
import { GlobalNav } from "../shared/GlobalNav";
import { CreateProjectModal } from "./CreateProjectModal";
import { InvoiceEditForm } from "../invoices/InvoicesComponents";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";
import { 
  Calendar, CreditCard, FileText, Users, Copy, Check, Eye, EyeOff, 
  ExternalLink, Edit3, Archive, Trash2, RotateCcw, AlertTriangle,
  Clock, CheckCircle, XCircle, ArrowRight, Settings, Plus, Link,
  Send, Shield, MoreHorizontal, Banknote,
  PiggyBank, Award, HelpCircle, Zap, TrendingUp, RefreshCw, Upload,
  Mail, AlertCircle, ChevronDown, Circle, Info, Crown, Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectCard = ({ project, onUpdate, onClick }) => {
  const { toast } = useToast();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [projectToComplete, setProjectToComplete] = useState(null);

  // Get payment method display info
  const getPaymentMethodInfo = (project) => {
    // PRIORITY: Always use client_chosen_structure first (what client actually selected during checkout)
    // This handles projects with conflicting is_invoice_project=True + client_chosen_structure data states
    // Fall back to payment_structure (what was set during project creation)
    const chosenStructure = project.client_chosen_structure || project.payment_structure;
    
    const paymentMethods = {
      full_upfront: {
        name: '100% Upfront',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        dotColor: 'bg-purple-500',
        icon: '💳'
      },
      fifty_fifty: {
        name: '50/50 Split',
        color: 'bg-green-100 text-green-700 border-green-200',
        dotColor: 'bg-green-500',
        icon: '⚖️'
      },
      three_month_milestones: {
        name: 'Monthly Installments',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        dotColor: 'bg-blue-500',
        icon: '📅'
      },
      credit_card: {
        name: 'Credit Card',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        dotColor: 'bg-amber-500',
        icon: '💎'
      },
      bnpl: {
        name: 'BNPL (Buy Now, Pay Later)',
        color: 'bg-pink-100 text-pink-700 border-pink-200',
        dotColor: 'bg-pink-500',
        icon: '⏰'
      },
      invoice: {
        name: 'Invoice (14 days)',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        dotColor: 'bg-blue-500',
        icon: '📄'
      }
    };

    return paymentMethods[chosenStructure] || {
      name: 'Payment Method TBD',
      color: 'bg-gray-100 text-gray-700 border-gray-200',
      dotColor: 'bg-gray-500',
      icon: '❓'
    };
  };
  
  const getStatusInfo = (project) => {
    const paymentMethod = getPaymentMethodInfo(project);
    
    if (project.status === 'archived') {
      return { 
        label: 'Archived', 
        color: 'bg-gray-100 text-gray-700',
        dotColor: 'bg-gray-500'
      };
    }
    
    // Manual completion status - only projects explicitly marked as completed
    if (project.status === 'completed') {
      return { 
        label: 'Work Delivered', 
        color: 'bg-gray-100 text-gray-700',
        dotColor: 'bg-gray-500'
      };
    }
    
    // Handle invoice projects ONLY if no client choice has been made
    // If client_chosen_structure exists, prioritize it over is_invoice_project flag
    if (project.is_invoice_project && !project.client_chosen_structure) {
      // Check if invoice has been paid by looking at milestones
      const paidMilestones = project.milestones.filter(m => m.status === 'paid').length;
      const totalMilestones = project.milestones.length;
      const invoicePaid = project.invoice_paid_at || (paidMilestones > 0 && paidMilestones === totalMilestones);
      
      if (invoicePaid) {
        return { 
          label: 'Invoice Paid - Work In Progress', 
          color: 'bg-blue-100 text-blue-700',  // Use blue for paid invoice projects
          dotColor: 'bg-blue-500'
        };
      } else {
        return { 
          label: 'Invoice Ready - Awaiting Payment', 
          color: 'bg-gray-100 text-gray-700',  // Use gray for unpaid invoice projects
          dotColor: 'bg-gray-500'
        };
      }
    }
    
    // Check contract and payment status for regular projects
    const contractSigned = project.contract_agreed;
    const paidMilestones = project.milestones.filter(m => m.status === 'paid').length;
    const totalMilestones = project.milestones.length;
    const allPaymentsReceived = paidMilestones === totalMilestones && totalMilestones > 0;
    
    if (!contractSigned) {
      return { 
        label: 'Awaiting Client Action', 
        color: 'bg-red-100 text-red-700',  // Keep red for awaiting client action
        dotColor: 'bg-red-500'
      };
    } else if (contractSigned && !allPaymentsReceived) {
      return { 
        label: `Contract Signed - ${paymentMethod.name} Selected`, 
        color: paymentMethod.color,  // Use client's chosen payment method color
        dotColor: paymentMethod.dotColor
      };
    } else if (allPaymentsReceived) {
      // For BNPL, always show the BNPL status even when paid
      const chosenStructure = project.client_chosen_structure || project.payment_structure;
      if (chosenStructure === 'bnpl') {
        return { 
          label: `Contract Signed - ${paymentMethod.name} Selected`, 
          color: paymentMethod.color,
          dotColor: paymentMethod.dotColor
        };
      }
      return { 
        label: `All Payments Received - Work In Progress`, 
        color: paymentMethod.color,  // Use client's chosen payment method color
        dotColor: paymentMethod.dotColor
      };
    } else {
      return { 
        label: 'In Progress', 
        color: paymentMethod.color,  // Use client's chosen payment method color
        dotColor: paymentMethod.dotColor
      };
    }
  };

  const handlePreviewInvoice = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoiceData(response.data);
      setShowInvoicePreview(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load invoice preview",
        variant: "destructive"
      });
    }
  };

  const handleSendInvoice = async () => {
    setSendingInvoice(true);
    try {
      await axios.post(`${API}/projects/${project.id}/invoice/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Invoice Sent",
        description: "Invoice has been sent to client for payment choice"
      });
      
      setShowInvoicePreview(false);
      onUpdate(); // Refresh project list
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send invoice",
        variant: "destructive"
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  const getMilestoneStatus = (milestone) => {
    if (milestone.status === 'paid') {
      return { label: 'Paid', color: 'text-green-600', date: formatDate(milestone.paid_date) };
    }
    if (milestone.due_date && isOverdue(milestone.due_date)) {
      return { label: `Overdue since ${formatDate(milestone.due_date)}`, color: 'text-red-600' };
    }
    if (milestone.due_date) {
      return { label: `Due ${formatDate(milestone.due_date)}`, color: 'text-blue-600' };
    }
    return { label: 'Pending', color: 'text-gray-600' };
  };

  const handleUnarchive = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/projects/${project.id}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Project restored",
        description: "Project has been moved back to active projects"
      });
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to restore project",
        variant: "destructive"
      });
    }
  };

  const handleCompleteProject = async (projectId) => {
    // Set the project to complete and show confirmation dialog
    setProjectToComplete(projectId);
    setShowCompleteDialog(true);
  };

  const confirmCompleteProject = async () => {
    try {
      await axios.post(`${API}/projects/${projectToComplete}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Work Completed",
        description: "Project has been marked as completed and moved to the Completed tab"
      });
      setShowCompleteDialog(false);
      setProjectToComplete(null);
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to complete project",
        variant: "destructive"
      });
    }
  };

  const handleReactivateProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Project Reactivated",
        description: "Project has been moved back to active projects"
      });
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reactivate project",
        variant: "destructive"
      });
    }
  };

  const handleArchiveProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Project Archived",
        description: "Project has been archived successfully"
      });
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to archive project",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Project Deleted",
        description: "Project has been permanently deleted"
      });
      setShowDeleteDialog(false);
      onUpdate();
    } catch (error) {
      toast({
        title: "Cannot Delete Project",
        description: error.response?.data?.detail || "Failed to delete project",
        variant: "destructive"
      });
      setShowDeleteDialog(false);
    }
  };

  const statusInfo = getStatusInfo(project);
  const paidAmount = project.milestones.filter(m => m.status === 'paid').reduce((sum, m) => sum + m.amount, 0);
  
  // Calculate total with VAT if VAT is enabled (to match milestone amounts which include VAT)
  const totalWithVat = project.vat_enabled 
    ? project.total_amount * (1 + (project.vat_rate || 20) / 100) 
    : project.total_amount;
  
  // Calculate progress - cap at 100% to prevent overflow
  const progress = Math.min((paidAmount / totalWithVat) * 100, 100);

  return (
    <Card 
      className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
      onClick={(e) => {
        // Only trigger card onClick if not clicking on action buttons
        if (!e.target.closest('button')) {
          onClick && onClick(e);
        }
      }}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 font-heading">{project.title}</h3>
              
              {/* Enhanced Status Badge with Color Indicator */}
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${statusInfo.dotColor}`}></div>
                <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              </div>
              
              {!project.contract_agreed && project.status !== 'archived' && (
                <Badge variant="outline" className="font-body">Contract pending</Badge>
              )}
              {project.status === 'archived' && (
                <button
                  onClick={handleUnarchive}
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-body"
                >
                  Restore
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3 font-body">
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <div className="flex flex-col">
                  <span>{project.client_name}</span>
                  <span className="text-xs text-gray-500">{project.client_email}</span>
                </div>
              </div>
              <div>
                £{project.total_amount.toLocaleString()}
              </div>
              <div>
                Created {formatDate(project.created_at)}
              </div>
              
              {/* Signed Contract Status */}
              {project.contract_signed && (
                <div className="flex items-center space-x-1 text-green-600">
                  <span className="w-4 h-4">📝</span>
                  <span className="text-xs font-medium">Signed</span>
                </div>
              )}
            </div>

            {/* Payment Progress for Selected Method */}
            {project.milestones && project.milestones.length > 0 && (
              <div className="mb-3 p-2 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">Payment Progress:</span>
                  <span className="text-xs text-gray-600">
                    {project.milestones.filter(m => m.status === 'paid').length} of {project.milestones.length} payments received
                  </span>
                </div>
                <div className="flex space-x-1">
                  {project.milestones.map((milestone, index) => (
                    <div
                      key={milestone.id || index}
                      className={`flex-1 h-2 rounded-full ${
                        milestone.status === 'paid' ? 'bg-green-500' : 
                        milestone.status === 'pending' ? 'bg-yellow-300' : 
                        'bg-gray-300'
                      }`}
                      title={`£${milestone.amount} - ${milestone.status}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Payment Link - FIRST */}
            <div className="mb-3 p-2 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm">
                  <Link className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-700 font-body">Direct Payment Link:</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(project.hosted_url, toast, "Payment link copied - share with client");
                  }}
                  className="text-green-600 hover:text-green-800 p-1 h-auto font-body"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="text-xs font-mono text-green-800 mt-1 bg-white rounded px-2 py-1">
                {project.hosted_url}
              </div>
              <div className="text-xs text-green-700 mt-1 font-body">
                Send this link directly to clients for immediate payment access
              </div>
            </div>

            {/* Embedded Payment Reference - Only show for Embedded plan subscribers */}
            {user?.subscription_plan === 'embedded' && (
              <div className="mb-3 p-2 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm">
                    <Copy className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-700 font-body">Embed Reference Code:</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(project.slug, toast, "Embed reference copied - for website integration");
                    }}
                    className="text-purple-600 hover:text-purple-800 p-1 h-auto font-body"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <div className="text-xs font-mono text-purple-800 mt-1 bg-white rounded px-2 py-1">
                  {project.slug}
                </div>
                <div className="text-xs text-purple-700 mt-1 font-body">
                  Use this code for website payment widgets and embed integration
                </div>
              </div>
            )}

            {/* VAT Info */}
            <div className="mb-3">
              <div className="flex items-center space-x-2 text-sm">
                <PiggyBank className="w-4 h-4 text-green-600" />
                <span className="text-gray-600 font-body">
                  {project.vat_enabled ? 
                    `VAT (${project.vat_rate}%): £${Math.round(project.total_amount * (project.vat_rate / 100)).toLocaleString()}` :
                    'VAT: Not applicable'
                  }
                </span>
              </div>
            </div>

            {/* Payment Progress Indicators */}
            {!project.is_invoice_project && project.milestones && project.milestones.length > 0 && (
              <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-700 text-sm font-body">Payment Progress</span>
                  </div>
                  <span className="text-xs text-blue-600 font-body">
                    {project.milestones.filter(m => m.status === 'paid').length} of {project.milestones.length} paid
                  </span>
                </div>
                <div className="space-y-2">
                  {project.milestones.map((milestone, index) => (
                    <div key={milestone.id || index} className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        {milestone.status === 'paid' ? (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        ) : (
                          <Circle className="w-3 h-3 text-gray-400" />
                        )}
                        <span className="font-body text-gray-700">{milestone.title}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium font-body ${milestone.status === 'paid' ? 'text-green-600' : 'text-gray-600'}`}>
                          £{milestone.amount?.toLocaleString() || '0'}
                        </span>
                        {milestone.status === 'paid' && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            Paid
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {progress > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600 font-body">
                  <span>£{paidAmount.toLocaleString()} received</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-green-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-2 ml-6">
            {/* Invoice Buttons - Only for invoice projects */}
            {project.is_invoice_project && (
              <>
                {/* Open Invoice Editor */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Open Invoice Edit button clicked, navigating to Invoice tab');
                    navigate(`/projects/${project.id}?tab=invoice`);
                  }}
                  className="text-blue-600 hover:text-blue-900"
                  title="Open Invoice Editor"
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
                
                {/* Preview Invoice Button - Only when invoice is generated */}
                {project.invoice_number && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewInvoice();
                    }}
                    className="text-blue-600 hover:text-blue-900"
                    title="Preview Invoice"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
              </>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('View as Client clicked for:', project.title, 'URL:', project.hosted_url);
                if (project.hosted_url) {
                  window.open(project.hosted_url, '_blank');
                } else {
                  console.error('No hosted_url for project:', project);
                }
              }}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2"
              title="View as Client"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>

            {/* PROJECT STATUS ACTIONS */}
            {/* Restore original Mark as Completed Button - icon only style */}
            {((project.is_invoice_project) || 
              (!project.is_invoice_project && project.milestones && project.milestones.length > 0)) && 
               project.status !== 'completed' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCompleteProject(project.id);
                }}
                className={`${project.status === 'completed' ? 'text-green-600 hover:text-green-900' : 'text-gray-600 hover:text-green-600'}`}
                title="Mark as Completed"
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}

            {/* Reactivate Button - Only show for completed projects */}
            {project.status === 'completed' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReactivateProject(project.id);
                }}
                className="text-blue-600 hover:text-blue-900"
                title="Reactivate Project"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            )}
            
            {/* Signed Contract Download Button */}
            {project.contract_signed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  const contractUrl = `${API}/contracts/${project.slug}/signed-pdf`;
                  window.open(contractUrl, '_blank');
                }}
                className="text-[#e6007e] hover:text-[#c4006b]"
                title="Download Signed Contract"
              >
                <FileText className="w-4 h-4" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleArchiveProject(project.id);
              }}
              className="text-yellow-600 hover:text-yellow-900"
              title="Archive Project"
            >
              <Archive className="w-4 h-4" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteDialog(true);
              }}
              className="text-red-600 hover:text-red-900"
              title="Delete Project"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            
            {/* EMAIL BUTTONS */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const emailData = {
                  to: project.client_email,
                  subject: `Payment Link for ${project.title}`,
                  template: 'payment_link',
                  project_id: project.id,
                  payment_url: project.hosted_url
                };
                sendEmail(emailData, toast);
              }}
              className="text-green-600 hover:text-green-900"
              title="Email Payment Link"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
      
      {/* Invoice Preview Modal */}
      {showInvoicePreview && invoiceData && (
        <Dialog open={showInvoicePreview} onOpenChange={setShowInvoicePreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Invoice Preview - {invoiceData.invoice_number}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Invoice Content */}
              <div className="bg-white rounded-lg border shadow-sm">
                <div 
                  dangerouslySetInnerHTML={{ __html: invoiceData.invoice_content }}
                  className="invoice-preview"
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600 font-body">
                  <p>Invoice will be sent to: <strong>{project.client_email}</strong></p>
                  <p>Client will receive payment link to choose payment structure</p>
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowInvoicePreview(false)}
                    className="font-body"
                  >
                    Close Preview
                  </Button>
                  <Button
                    onClick={handleSendInvoice}
                    disabled={sendingInvoice}
                    className="bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
                  >
                    {sendingInvoice ? "Sending..." : "Send to Client"}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Delete Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Are you sure you want to delete <strong>{project.title}</strong>? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800 font-body">
                    All project data will be permanently lost.
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteProject}
                  className="bg-red-600 hover:bg-red-700 text-white font-body"
                >
                  Delete Project
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Complete Work Confirmation Dialog */}
      {showCompleteDialog && (
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Mark Work as Complete</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Are you sure you want to mark the work for <strong>{project.title}</strong> as delivered and complete?
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800 font-body">
                    This will move the project to the "Completed" tab.
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCompleteDialog(false);
                    setProjectToComplete(null);
                  }}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmCompleteProject}
                  className="bg-green-600 hover:bg-green-700 text-white font-body"
                >
                  Mark as Complete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Delete Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Are you sure you want to delete <strong>{project.title}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800 font-body">
                    This action cannot be undone. All project data will be permanently lost.
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteProject}
                  className="bg-red-600 hover:bg-red-700 text-white font-body"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Complete Work Confirmation Dialog */}
      {showCompleteDialog && (
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Mark Work as Complete</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Are you sure you want to mark the work for <strong>{project.title}</strong> as delivered and complete?
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800 font-body">
                    This will move the project to the "Completed" tab.
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCompleteDialog(false);
                    setProjectToComplete(null);
                  }}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmCompleteProject}
                  className="bg-green-600 hover:bg-green-700 text-white font-body"
                >
                  Mark as Complete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

const EmptyState = ({ filter, onCreateProject }) => {
  if (filter === 'archived') {
    return (
      <Card className="border-2 border-dashed border-gray-300 text-center py-12">
        <CardContent>
          <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No archived projects</h3>
          <p className="text-gray-600 font-body">Archived projects will appear here when you archive them.</p>
        </CardContent>
      </Card>
    );
  }

  if (filter === 'fulfilled') {
    return (
      <Card className="border-2 border-dashed border-gray-300 text-center py-12">
        <CardContent>
          <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No fulfilled projects</h3>
          <p className="text-gray-600 font-body">Completed projects with all payments received will appear here.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-dashed border-gray-300 text-center py-12">
      <CardContent>
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No chasing. All sorted.</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto font-body">
          Structure your first payment flow. Choose your payment structure, lock it in with a contract, 
          and send the payment link to your client.
        </p>
        <Button 
          onClick={onCreateProject}
          className="bg-black hover:bg-gray-800 text-white font-body"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Your First Project
        </Button>
      </CardContent>
    </Card>
  );
};

// Enhanced Create Project Modal

// CreateProjectModal extracted to ./components/


const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const { token, user, logout } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Handle tab parameter from URL (for Edit Invoice navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && (tabParam === 'invoice' || tabParam === 'edit' || tabParam === 'overview')) {
      setActiveTab(tabParam);
    }
  }, [location]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch project details",
        variant: "destructive"
      });
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="projects" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading project...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="projects" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/projects')}
              className="text-gray-600 hover:text-gray-900 font-body"
            >
              ← Back to Projects
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-2xl font-bold text-gray-900 font-heading">{project?.title}</h1>
            {project?.status === 'archived' && (
              <Badge className="bg-gray-100 text-gray-700">Archived</Badge>
            )}
          </div>
          
          {/* Primary Action Buttons + Mirrored Dashboard Icons - Top Right */}
          {project && (
            <div className="flex items-center space-x-3">
              {/* Mirror the dashboard row icons so the project page shows the
                  same quick actions you saw on the dashboard card. */}
              <div className="flex items-center space-x-1 pr-3 border-r border-gray-200">
                {(project.is_invoice_project || project.invoice_number) && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab('invoice')}
                      className="text-blue-600 hover:text-blue-900"
                      title="Open Invoice Editor"
                      data-testid="project-header-edit-invoice-btn"
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/projects/${project.id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
                          const w = window.open('', '_blank');
                          if (w && res.data?.invoice_content) {
                            w.document.write(res.data.invoice_content);
                            w.document.close();
                          }
                        } catch (e) {
                          toast({ title: "Preview failed", description: e.response?.data?.detail || "Could not load preview", variant: "destructive" });
                        }
                      }}
                      className="text-blue-600 hover:text-blue-900"
                      title="Preview Invoice"
                      data-testid="project-header-preview-invoice-btn"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(`/checkout/${project.slug}`, '_blank')}
                  className="text-gray-600 hover:text-gray-900"
                  title="View as Client"
                  data-testid="project-header-view-as-client-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
                {project.contract_signed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`/api/projects/${project.id}/contract/download?token=${token}`, '_blank')}
                    className="text-emerald-600 hover:text-emerald-900"
                    title="Download Signed Contract"
                    data-testid="project-header-download-contract-btn"
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                )}
                {project.status !== 'archived' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm('Archive this project? Client checkout will be disabled.')) return;
                      try {
                        await axios.put(`${API}/projects/${project.id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast({ title: "Archived", description: "Project archived." });
                        fetchProject();
                      } catch (e) {
                        toast({ title: "Error", description: e.response?.data?.detail || "Failed to archive", variant: "destructive" });
                      }
                    }}
                    className="text-gray-500 hover:text-gray-900"
                    title="Archive project"
                    data-testid="project-header-archive-btn"
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (!project.client_email) {
                      toast({ title: "No client email", description: "This project has no client email on file.", variant: "destructive" });
                      return;
                    }
                    try {
                      await axios.post(`${API}/projects/${project.id}/send-payment-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast({ title: "Sent", description: `Payment link emailed to ${project.client_email}.` });
                    } catch (e) {
                      toast({ title: "Error", description: e.response?.data?.detail || "Failed to send", variant: "destructive" });
                    }
                  }}
                  className="text-emerald-600 hover:text-emerald-900"
                  title="Email payment link to client"
                  data-testid="project-header-send-link-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm(`Permanently delete "${project.title}"? This cannot be undone.`)) return;
                    try {
                      await axios.delete(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
                      toast({ title: "Deleted", description: "Project deleted." });
                      navigate('/');
                    } catch (e) {
                      toast({ title: "Error", description: e.response?.data?.detail || "Failed to delete", variant: "destructive" });
                    }
                  }}
                  className="text-rose-500 hover:text-rose-700"
                  title="Delete project"
                  data-testid="project-header-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {/* Existing primary action buttons */}
              <CompleteAndRequestPaymentButton project={project} onSuccess={fetchProject} />
              <CreateInvoiceFromProjectButton project={project} onSuccess={fetchProject} />
            </div>
          )}
        </div>

        {project?.status === 'archived' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Archive className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-800 font-body">
                <strong>Archived</strong> — client checkout is disabled
              </span>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' }); }} className="space-y-6">
          <TabsList className={`grid w-full max-w-md ${(project?.is_invoice_project || project?.invoice_number) ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <TabsTrigger value="overview" className="font-body">Overview</TabsTrigger>
            <TabsTrigger value="edit" className="font-body">Edit Project</TabsTrigger>
            {(project?.is_invoice_project || project?.invoice_number) && (
              <TabsTrigger value="invoice" className="font-body">Invoice</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview">
            <ClientProgressMirror project={project} onUpdate={fetchProject} />
          </TabsContent>

          <TabsContent value="edit">
            <ProjectEditForm project={project} onUpdate={fetchProject} onSuccess={() => setActiveTab("overview")} />
          </TabsContent>

          {(project?.is_invoice_project || project?.invoice_number) && (
            <TabsContent value="invoice">
              <InvoiceEditForm project={project} onUpdate={fetchProject} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

// Create Quote From Project Button Component

const CreateQuoteFromProjectButton = ({ project, onSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();

  const handleCreateQuote = () => setShowModal(true);

  return (
    <>
      <Button
        onClick={handleCreateQuote}
        className="bg-yellow-600 hover:bg-yellow-700 text-white font-body"
      >
        <FileText className="w-4 h-4 mr-2" />
        Create Quote
      </Button>

      {showModal && (
        <CreateQuoteFromProjectModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          project={project}
          onSuccess={() => {
            setShowModal(false);
            onSuccess();
            toast({
              title: "Quote Created",
              description: "Quote has been created successfully from project details"
            });
          }}
        />
      )}
    </>
  );
};

// Create Invoice From Project Button Component  
const CreateInvoiceFromProjectButton = ({ project, onSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleCreateInvoice = () => {
    // If already an invoice project, redirect to edit
    if (project.is_invoice_project) {
      navigate(`/projects/${project.id}?tab=invoice`);
      return;
    }
    setShowModal(true);
  };

  return (
    <>
      <Button
        onClick={handleCreateInvoice}
        className="bg-blue-600 hover:bg-blue-700 text-white font-body"
      >
        <FileText className="w-4 h-4 mr-2" />
        {project.is_invoice_project ? 'Edit Invoice' : 'Create Invoice'}
      </Button>

      {showModal && (
        <CreateInvoiceFromProjectModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          project={project}
          onSuccess={() => {
            setShowModal(false);
            onSuccess();
            toast({
              title: "Invoice Created",
              description: "Invoice has been created successfully from project details"
            });
          }}
        />
      )}
    </>
  );
};

// Upload Invoice Button Component
const UploadInvoiceButton = ({ project, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.includes('pdf')) {
      toast({
        title: "Invalid File",
        description: "Please upload a PDF file only",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/projects/${project.id}/invoice/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Invoice Uploaded",
        description: "Invoice PDF has been uploaded successfully"
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload invoice",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="font-body bg-pink-50 hover:bg-pink-100 text-pink-700 border-pink-200"
      >
        <Upload className="w-4 h-4 mr-2" />
        {uploading ? 'Uploading...' : 'Upload Invoice'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleUpload}
        className="hidden"
      />
    </>
  );
};

// Link Accounting Software Button Component
const LinkAccountingSoftwareButton = ({ project, onSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();

  const handleLinkSoftware = () => setShowModal(true);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleLinkSoftware}
        className="font-body bg-violet-50 hover:bg-violet-100 text-violet-700 border-violet-200"
      >
        <Link className="w-4 h-4 mr-2" />
        Link Accounting
      </Button>

      {showModal && (
        <LinkAccountingSoftwareModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          project={project}
          onSuccess={() => {
            setShowModal(false);
            onSuccess();
            toast({
              title: "Integration Linked",
              description: "Accounting software has been linked successfully"
            });
          }}
        />
      )}
    </>
  );
};

// Client Progress Mirror Component

const ClientProgressMirror = ({ project, onUpdate }) => {
  const [showContract, setShowContract] = useState(false);
  const [contractContent, setContractContent] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();
  const { token } = useAuth();
  const navigate = useNavigate();

  const getMilestoneStatus = (milestone) => {
    if (milestone.status === 'paid') {
      return {
        label: 'Paid',
        color: 'bg-green-100 text-green-700',
        date: milestone.paid_date ? formatDate(milestone.paid_date) : null
      };
    }

    if (milestone.due_date) {
      const overdue = isOverdue(milestone.due_date);
      if (overdue) {
        return {
          label: `Overdue since ${formatDate(milestone.due_date)}`,
          color: 'bg-red-100 text-red-700',
          date: null
        };
      } else {
        return {
          label: `Due ${formatDate(milestone.due_date)}`,
          color: 'bg-blue-100 text-blue-700',
          date: null
        };
      }
    }

    return {
      label: 'Pending',
      color: 'bg-gray-100 text-gray-700',
      date: null
    };
  };

  const getMilestoneLabel = (index, total, structure) => {
    if (structure === 'fifty_fifty') {
      return index === 0 ? '1st' : '2nd';
    }
    if (structure === 'three_month_milestones') {
      const labels = ['1st', '2nd', '3rd'];
      return labels[index] || `${index + 1}th`;
    }
    return '1st';
  };

  const handleViewContract = async () => {
    try {
      const response = await axios.get(`${API}/checkout/${project.slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractContent(response.data.contract.content);
      setShowContract(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load contract",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Peace of Mind Dashboard */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-black to-gray-800 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-400 rounded-full p-2">
                <Shield className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-heading text-yellow-400">PEACE OF MIND</h2>
                <p className="text-sm text-gray-300 font-body">Structured • Protected • Paid</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-300 font-body">Status</div>
              <div className="text-lg font-bold text-green-400 font-heading">PROTECTED</div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-1" />
                <span className="text-sm font-medium text-yellow-400 font-body">Contract Locked</span>
              </div>
              <p className="text-xs text-gray-300 font-body">
                {project.contract_agreed ? 'Signed & Protected' : 'Awaiting Signature'}
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-1" />
                <span className="text-sm font-medium text-yellow-400 font-body">Payment Structured</span>
              </div>
              <p className="text-xs text-gray-300 font-body">
                {getPaymentStructureLabel(project.payment_structure)}
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-1" />
                <span className="text-sm font-medium text-yellow-400 font-body">No Chasing Required</span>
              </div>
              <p className="text-xs text-gray-300 font-body">Automated & Protected</p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-600 text-center">
            <p className="text-sm text-gray-300 font-body">
              <strong className="text-yellow-400">Professional setup eliminates payment delays</strong> — No more chasing, everything automated
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Client Info Header */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-gray-900 font-heading">{project.title}</CardTitle>
              <div className="flex items-center space-x-4 mt-2 text-gray-600 font-body">
                <span>{project.client_name}</span>
                <span>•</span>
                <span>{project.client_email}</span>
                {project.client_company && (
                  <>
                    <span>•</span>
                    <span>{project.client_company}</span>
                  </>
                )}
              </div>
              {/* Professional Trust Badge */}
              <div className="flex items-center space-x-2 mt-2">
                <Badge className="bg-blue-100 text-blue-700 text-xs font-body">
                  <Award className="w-3 h-3 mr-1" />
                  Paify Verified Professional
                </Badge>
                <Badge className="bg-green-100 text-green-700 text-xs font-body">
                  <Shield className="w-3 h-3 mr-1" />
                  Payment Protected
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 font-heading">
                £{project.total_amount.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 font-body">
                {getPaymentStructureLabel(project.payment_structure)}
              </div>
              <div className="text-xs text-green-600 font-body mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                0 hours spent chasing
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Main Action Buttons - Single Line with Icons + Labels */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(project.hosted_url, toast, "Payment link copied")}
              className="font-body bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(project.hosted_url, '_blank')}
              className="font-body bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Preview Client Experience
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const emailData = {
                  to: project.client_email,
                  subject: `Payment Link for ${project.title}`,
                  template: 'payment_link',
                  project_id: project.id,
                  payment_url: project.hosted_url
                };
                sendEmail(emailData, toast);
              }}
              className="font-body bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <Send className="w-4 h-4 mr-2" />
              Email Payment Link
            </Button>

            {/* Invoice Project Buttons */}
            {project.is_invoice_project && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/projects/${project.id}?tab=invoice`)}
                  className="font-body bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit Invoice
                </Button>
                
                {project.invoice_number && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/projects/${project.id}/invoice`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (response.data.invoice_content) {
                          const newWindow = window.open('', '_blank');
                          newWindow.document.write(response.data.invoice_content);
                          newWindow.document.close();
                        } else {
                          toast({ title: "Invoice not found", description: "No invoice content available for this project", variant: "destructive" });
                        }
                      } catch (error) {
                        toast({ title: "Error", description: "Failed to load invoice preview", variant: "destructive" });
                      }
                    }}
                    className="font-body bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Invoice
                  </Button>
                )}
              </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                toast({ title: "Link Rotated", description: "New payment link generated" });
              }}
              className="font-body bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Rotate Link
            </Button>
          </div>

          {/* Document Management Section */}
          {project && (
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <FileText className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900 font-heading">Professional Document Management</h3>
                <Badge className="bg-blue-100 text-blue-700 text-xs font-body">Integrated</Badge>
              </div>
              <p className="text-xs text-gray-600 mb-3 font-body">
                Streamline your accounting workflow with integrated document management and software connections
              </p>
              <div className="flex flex-wrap gap-3">
                <UploadInvoiceButton project={project} onSuccess={onUpdate} />
                <LinkAccountingSoftwareButton project={project} onSuccess={onUpdate} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contract Status - Enhanced */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-heading">Service Agreement</span>
            <Badge variant="secondary" className="font-body">v{project.contract_version}</Badge>
            {project.contract_agreed && (
              <Badge className="bg-green-100 text-green-700 font-body">
                <Shield className="w-3 h-3 mr-1" />
                Legally Protected
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {project.contract_agreed ? (
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <span className="text-green-700 font-medium font-body">
                        Signed by {project.contract_signature || 'Client'} ✓ on {formatDate(project.contract_signed_at)}
                      </span>
                      <div className="text-xs text-green-600 font-body mt-1">
                        ✓ Both parties protected • Terms locked in • No scope creep
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <div>
                      <span className="text-yellow-700 font-medium font-body">Awaiting client signature</span>
                      <div className="text-xs text-gray-600 font-body mt-1">
                        Contract ready • Terms agreed • Protection pending signature
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleViewContract}
                className="font-body"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Contract
              </Button>
            </div>
            
            {project.contract_agreed && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center space-x-2 text-green-800 text-sm font-body">
                  <Award className="w-4 h-4" />
                  <span><strong>Professional Protection:</strong> Contract locks in terms, prevents scope creep, and ensures payment security</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment Instalments - Enhanced */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Banknote className="w-5 h-5 text-green-600" />
            <span className="font-heading">Structured Payment Schedule</span>
            <Badge className="bg-blue-100 text-blue-700 font-body">
              <TrendingUp className="w-3 h-3 mr-1" />
              Cash Flow Optimized
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Payment Confidence Summary */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900 font-heading">Payment Confidence Status</h4>
                  <p className="text-sm text-gray-600 font-body">
                    {project.milestones.filter(m => m.status === 'paid').length} of {project.milestones.length} payments received
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600 font-heading">
                    £{project.milestones.filter(m => m.status === 'paid').reduce((sum, m) => sum + m.amount, 0).toLocaleString()} secured
                  </div>
                  <div class="text-xs text-green-600 font-body">No chasing required ✓</div>
                </div>
              </div>
            </div>

            {project.milestones.map((milestone, index) => {
              const status = getMilestoneStatus(milestone);
              const label = getMilestoneLabel(index, project.milestones.length, project.payment_structure);
              const isPaid = milestone.status === 'paid';
              
              return (
                <div key={milestone.id} className={`border rounded-lg p-4 ${isPaid ? 'bg-green-50 border-green-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg font-bold text-gray-900 font-heading">
                        {label} Payment
                      </span>
                      <Badge className={status.color}>{status.label}</Badge>
                      {isPaid && (
                        <Badge className="bg-green-100 text-green-700 text-xs font-body">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Protected
                        </Badge>
                      )}
                    </div>
                    <span className="text-xl font-bold text-gray-900 font-heading">
                      £{milestone.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-3 font-body">
                    {milestone.title}
                  </div>
                  {milestone.due_date && (
                    <div className="text-sm text-gray-500 font-body">
                      Due: {formatDate(milestone.due_date)}
                    </div>
                  )}
                  {status.date && (
                    <div className="text-sm text-green-600 font-body">
                      ✓ Paid: {status.date} — Automatically processed, no chasing required
                    </div>
                  )}
                  {!isPaid && milestone.due_date && (
                    <div className="text-xs text-blue-600 font-body mt-2">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Automated reminders active • Payment protection enabled
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          

        </CardContent>
      </Card>

      {/* Project Info - Enhanced with Business Impact */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading">Professional Project Management</CardTitle>
          <p className="text-sm text-gray-600 font-body">Paify's structured approach eliminates payment delays and reduces administrative overhead</p>
        </CardHeader>
        <CardContent>


          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 font-body">Created:</span>
              <div className="font-medium font-body">{formatDate(project.created_at)}</div>
            </div>
            <div>
              <span className="text-gray-500 font-body">VAT Management:</span>
              <div className="font-medium font-body">{project.vat_rate}% (Auto-separated)</div>
            </div>
            <div>
              <span className="text-gray-500 font-body">Payment Security:</span>
              <div className="font-medium font-body">{project.provider} Protected</div>
            </div>
            <div>
              <span className="text-gray-500 font-body">Professional Status:</span>
              <div className="font-medium font-body capitalize">
                {project.status}
                {project.status === 'active' && (
                  <Badge className="ml-2 bg-green-100 text-green-700 text-xs">
                    <Shield className="w-3 h-3 mr-1" />
                    Protected
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {project.custom_scope && (
            <div className="mt-4">
              <span className="text-gray-500 text-sm font-body">Project Scope:</span>
              <div className="mt-1 text-sm text-gray-700 font-body">{project.custom_scope}</div>
            </div>
          )}

          {/* Business Impact Metrics */}
          <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 font-heading">Business Impact</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-purple-600 font-heading">0hrs</div>
                <div className="text-xs text-gray-600 font-body">Chasing Payments</div>
                <div className="text-xs text-green-600 font-body">vs. 21-30hrs industry avg</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600 font-heading">100%</div>
                <div className="text-xs text-gray-600 font-body">Payment Protection</div>
                <div className="text-xs text-green-600 font-body">Contract guaranteed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 font-heading">£0</div>
                <div className="text-xs text-gray-600 font-body">Late Payment Risk</div>
                <div className="text-xs text-green-600 font-body">Structured & secured</div>
              </div>
            </div>
          </div>

          {/* Project Lifecycle Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  toast({ title: "Project Completed", description: "Project marked as completed" });
                }}
                className="font-body bg-teal-50 hover:bg-teal-100 text-teal-700 border-teal-200"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete Project
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  toast({ title: "Project Archived", description: "Project has been archived" });
                }}
                className="font-body bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive Project
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="font-body bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Project
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contract Dialog */}
      {showContract && (
        <Dialog open={showContract} onOpenChange={setShowContract}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Service Agreement - v{project.contract_version}</DialogTitle>
            </DialogHeader>
            <div className="bg-white rounded-lg border shadow-sm">
              <div 
                dangerouslySetInnerHTML={{ __html: contractContent }}
                className="contract-preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Delete Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Are you sure you want to delete <strong>{project.title}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800 font-body">
                    This action cannot be undone. All project data will be permanently lost.
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    try {
                      await axios.delete(`${API}/projects/${project.id}`, {
                        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                      });
                      toast({
                        title: "Project Deleted",
                        description: "Project has been permanently deleted"
                      });
                      navigate('/');
                    } catch (error) {
                      toast({
                        title: "Error", 
                        description: "Failed to delete project",
                        variant: "destructive"
                      });
                    }
                    setShowDeleteDialog(false);
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white font-body"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Project Actions Component

const ProjectActions = ({ project, onUpdate }) => {
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleArchive = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/projects/${project.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Project archived",
        description: "Project has been moved to archive"
      });
      onUpdate();
      setShowArchiveDialog(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to archive project",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/projects/${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Project deleted",
        description: "Project has been permanently deleted"
      });
      navigate('/');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete project",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRotateLink = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/rotate-slug`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Link rotated",
        description: "New payment link has been generated"
      });
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to rotate link",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRotateLink}
          disabled={loading}
          className="font-body"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Rotate Link
        </Button>
        
        {project.status !== 'archived' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowArchiveDialog(true)}
            className="text-yellow-600 hover:text-yellow-700 font-body"
          >
            <Archive className="w-4 h-4 mr-2" />
            Archive
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDeleteDialog(true)}
          className="text-red-600 hover:text-red-700 font-body"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete
        </Button>
      </div>

      {/* Archive Confirmation Dialog */}
      {showArchiveDialog && (
        <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Archive Project</DialogTitle>
            </DialogHeader>
            <p className="font-body">Are you sure you want to archive this project? The client checkout page will be disabled, but you can restore it later.</p>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowArchiveDialog(false)} className="font-body">
                Cancel
              </Button>
              <Button 
                onClick={handleArchive} 
                disabled={loading}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-body"
              >
                {loading ? "Archiving..." : "Archive"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Delete Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="font-body">Are you sure you want to permanently delete this project?</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800 font-body">
                    <strong>Warning:</strong> This action cannot be undone. All project data will be permanently lost.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="font-body">
                Cancel
              </Button>
              <Button 
                onClick={handleDelete} 
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white font-body"
              >
                {loading ? "Deleting..." : "Delete Permanently"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Complete & Request Final Payment Button Component (for 50/50 projects)
const CompleteAndRequestPaymentButton = ({ project, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  // Only show for 50/50 projects with scheduled payments pending
  const isEligible = project?.payment_structure === 'fifty_fifty' && 
    project?.has_scheduled_payments &&
    project?.milestones?.some(m => m.status === 'scheduled' || m.status === 'pending');

  console.log('CompleteAndRequestPaymentButton eligibility check:', {
    payment_structure: project?.payment_structure,
    has_scheduled_payments: project?.has_scheduled_payments,
    milestones: project?.milestones?.map(m => ({ title: m.title, status: m.status })),
    isEligible
  });

  if (!isEligible) {
    return null;
  }

  const handleTriggerPayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/trigger-final-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Final Payment Processed",
        description: "The client has been charged for the final instalment. Project marked as complete!",
      });
      
      setShowConfirmDialog(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: error.response?.data?.detail || "Failed to process final payment. The client may need to update their payment method.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowConfirmDialog(true)}
        className="bg-green-600 hover:bg-green-700 text-white font-body"
      >
        <CheckCircle className="w-4 h-4 mr-2" />
        Complete & Request Final Payment
      </Button>

      {showConfirmDialog && (
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Complete Project & Charge Final Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600 font-body">
                This will mark the project as complete and immediately charge the client's saved payment method for the final instalment.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 font-body">
                  <strong>Note:</strong> The client's card will be charged immediately. Make sure the work is complete before proceeding.
                </p>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowConfirmDialog(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleTriggerPayment}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white font-body"
                >
                  {loading ? "Processing..." : "Complete & Charge"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Create Quote From Project Modal Component

const CreateQuoteFromProjectModal = ({ isOpen, onClose, project, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: `Quote for ${project.title}`,
    client_name: project.client_name,
    client_email: project.client_email,
    client_company: project.client_company || '',
    work_description: project.custom_scope || '',
    total_amount: project.total_amount,
    include_deliverables: true,
    deliverables_text: '',
    delivery_timeline: '2-3 weeks',
    payment_terms: 'Net 30 days',
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
  });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/quotes`, {
        ...formData,
        project_reference: project.id // Link to original project
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Quote Created",
        description: "Quote has been created successfully from project details"
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create quote",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Quote from Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title" className="font-body">Quote Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="font-body"
                required
              />
            </div>
            <div>
              <Label htmlFor="total_amount" className="font-body">Total Amount (£)</Label>
              <Input
                id="total_amount"
                type="number"
                step="0.01"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) })}
                className="font-body"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="client_name" className="font-body">Client Name</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="font-body"
                required
              />
            </div>
            <div>
              <Label htmlFor="client_email" className="font-body">Client Email</Label>
              <Input
                id="client_email"
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                className="font-body"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="client_company" className="font-body">Client Company (Optional)</Label>
            <Input
              id="client_company"
              value={formData.client_company}
              onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
              className="font-body"
            />
          </div>

          <div>
            <Label htmlFor="work_description" className="font-body">Work Description</Label>
            <Textarea
              id="work_description"
              value={formData.work_description}
              onChange={(e) => setFormData({ ...formData, work_description: e.target.value })}
              rows={3}
              className="font-body"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="delivery_timeline" className="font-body">Delivery Timeline</Label>
              <Input
                id="delivery_timeline"
                value={formData.delivery_timeline}
                onChange={(e) => setFormData({ ...formData, delivery_timeline: e.target.value })}
                className="font-body"
                placeholder="e.g., 2-3 weeks"
              />
            </div>
            <div>
              <Label htmlFor="valid_until" className="font-body">Valid Until</Label>
              <Input
                id="valid_until"
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className="font-body"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include_deliverables"
              checked={formData.include_deliverables}
              onCheckedChange={(checked) => setFormData({ ...formData, include_deliverables: checked })}
            />
            <Label htmlFor="include_deliverables" className="font-body">Include detailed deliverables</Label>
          </div>

          {formData.include_deliverables && (
            <div>
              <Label htmlFor="deliverables_text" className="font-body">Deliverables</Label>
              <Textarea
                id="deliverables_text"
                value={formData.deliverables_text}
                onChange={(e) => setFormData({ ...formData, deliverables_text: e.target.value })}
                rows={3}
                className="font-body"
                placeholder="List the specific deliverables for this quote..."
              />
            </div>
          )}

          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="font-body"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-body"
            >
              {loading ? "Creating..." : "Create Quote"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Invoice From Project Modal Component
const CreateInvoiceFromProjectModal = ({ isOpen, onClose, project, onSuccess }) => {
  const [formData, setFormData] = useState({
    invoice_number: `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
    invoice_due_days: 30,
    invoice_type: 'generated',
    payment_terms: 'Net 30 days',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update project to make it an invoice project
      await axios.put(`${API}/projects/${project.id}`, {
        ...project,
        is_invoice_project: true,
        invoice_number: formData.invoice_number,
        invoice_due_days: formData.invoice_due_days,
        invoice_type: formData.invoice_type,
        status: 'active' // Invoice projects should be active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Invoice Created",
        description: "Project has been converted to an invoice project successfully"
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create invoice",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Invoice from Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="invoice_number" className="font-body">Invoice Number</Label>
            <Input
              id="invoice_number"
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              className="font-body"
              required
            />
          </div>

          <div>
            <Label htmlFor="invoice_due_days" className="font-body">Payment Due (Days)</Label>
            <Select 
              value={formData.invoice_due_days.toString()} 
              onValueChange={(value) => setFormData({ ...formData, invoice_due_days: parseInt(value) })}
            >
              <SelectTrigger className="font-body">
                <SelectValue placeholder="Select due days" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="21">21 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="payment_terms" className="font-body">Payment Terms</Label>
            <Input
              id="payment_terms"
              value={formData.payment_terms}
              onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
              className="font-body"
              placeholder="e.g., Net 30 days"
            />
          </div>

          <div>
            <Label htmlFor="notes" className="font-body">Additional Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="font-body"
              placeholder="Any additional notes for the invoice..."
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800 font-body">
              <strong>Note:</strong> This will convert the project to an invoice project with immediate payment collection. 
              The project status will change to "active" and skip client choice.
            </p>
          </div>

          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="font-body"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-body"
            >
              {loading ? "Creating..." : "Create Invoice"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Link Accounting Software Modal Component
const LinkAccountingSoftwareModal = ({ isOpen, onClose, project, onSuccess }) => {
  const [selectedSoftware, setSelectedSoftware] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const softwareOptions = [
    { id: 'xero', name: 'Xero', description: 'Cloud-based accounting software' },
    { id: 'quickbooks', name: 'QuickBooks', description: 'Small business accounting' },
    { id: 'sage', name: 'Sage', description: 'Enterprise accounting solution' },
    { id: 'freeagent', name: 'FreeAgent', description: 'Freelancer accounting software' }
  ];

  const handleConnect = async () => {
    if (!selectedSoftware) {
      toast({
        title: "Selection Required",
        description: "Please select an accounting software to connect",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // For now, we'll just simulate the connection process
      // In a real implementation, this would redirect to OAuth flows
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "Coming Soon",
        description: `${softwareOptions.find(s => s.id === selectedSoftware)?.name} integration is coming soon`,
      });
      onSuccess();
    } catch (error) {
      toast({
        title: "Connection Failed",
        description: "Failed to connect to accounting software",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Link Accounting Software</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 font-body">
            Connect your accounting software to automatically sync invoices and payments for this project.
          </p>

          <div className="space-y-3">
            {softwareOptions.map((software) => (
              <label
                key={software.id}
                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedSoftware === software.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="software"
                  value={software.id}
                  checked={selectedSoftware === software.id}
                  onChange={(e) => setSelectedSoftware(e.target.value)}
                  className="sr-only"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 font-heading">{software.name}</div>
                  <div className="text-sm text-gray-600 font-body">{software.description}</div>
                </div>
                <div className={`w-4 h-4 border-2 rounded-full ${
                  selectedSoftware === software.id
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {selectedSoftware === software.id && (
                    <div className="w-full h-full bg-white rounded-full scale-50"></div>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800 font-body">
              <strong>Coming Soon:</strong> Accounting software integrations are currently in development. 
              You can upload invoices manually for now.
            </p>
          </div>

          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="font-body"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={loading || !selectedSoftware}
              className="bg-green-600 hover:bg-green-700 text-white font-body"
            >
              {loading ? "Connecting..." : "Connect Software"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Project Edit Form Component

const ProjectEditForm = ({ project, onUpdate, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: project?.title || '',
    client_name: project?.client_name || '',
    client_email: project?.client_email || '',
    client_company: project?.client_company || '',
    client_billing_address: project?.client_billing_address || '',
    client_billing_city: project?.client_billing_city || '',
    client_billing_postcode: project?.client_billing_postcode || '',
    client_billing_country: project?.client_billing_country || 'United Kingdom',
    total_amount: project?.total_amount || '',
    payment_structure: project?.payment_structure || 'full_upfront',
    custom_scope: project?.custom_scope || '',
    vat_rate: project?.vat_rate || 20
  });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title,
        client_name: project.client_name,
        client_email: project.client_email,
        client_company: project.client_company || '',
        client_billing_address: project.client_billing_address || '',
        client_billing_city: project.client_billing_city || '',
        client_billing_postcode: project.client_billing_postcode || '',
        client_billing_country: project.client_billing_country || 'United Kingdom',
        total_amount: project.total_amount,
        payment_structure: project.payment_structure,
        custom_scope: project.custom_scope || '',
        vat_rate: project.vat_rate
      });
    }
  }, [project]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        currency: 'GBP', // Always use GBP
        total_amount: parseFloat(formData.total_amount)
      };

      await axios.put(`${API}/projects/${project.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Project updated",
        description: "Your project has been updated successfully"
      });
      
      onUpdate();
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update project",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const isFieldLocked = (fieldName) => {
    return project?.has_captured_payments && 
           ['total_amount', 'payment_structure'].includes(fieldName);
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="font-heading">Edit Project</CardTitle>
        {project?.has_captured_payments && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-800 font-body">
                Some fields are locked because this project has captured payments
              </span>
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title" className="font-body">Project Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="font-body"
                required
              />
            </div>

            <div>
              <Label htmlFor="total_amount" className="font-body">
                Total Amount
                {isFieldLocked('total_amount') && <span className="text-yellow-600 ml-1">🔒</span>}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">£</span>
                <Input
                  id="total_amount"
                  type="number"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  className="font-body pl-8"
                  disabled={isFieldLocked('total_amount')}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="client_name" className="font-body">Client Name</Label>
              <Input
                id="client_name"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="font-body"
                required
              />
            </div>

            <div>
              <Label htmlFor="client_email" className="font-body">Client Email</Label>
              <Input
                id="client_email"
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                className="font-body"
                required
              />
            </div>

            <div>
              <Label htmlFor="client_company" className="font-body">Client Company (Optional)</Label>
              <Input
                id="client_company"
                value={formData.client_company}
                onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                className="font-body"
              />
            </div>

            <div>
              <Label htmlFor="payment_structure" className="font-body">
                Payment Structure
                {isFieldLocked('payment_structure') && <span className="text-yellow-600 ml-1">🔒</span>}
              </Label>
              <Select 
                value={formData.payment_structure} 
                onValueChange={(value) => setFormData({ ...formData, payment_structure: value })}
                disabled={isFieldLocked('payment_structure')}
              >
                <SelectTrigger className="font-body">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_upfront">100% Upfront</SelectItem>
                  <SelectItem value="fifty_fifty">50/50 Split</SelectItem>
                  <SelectItem value="three_month_milestones">Monthly Instalments</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="bnpl" disabled>BNPL (Coming Soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="vat_rate" className="font-body">VAT Rate (%)</Label>
              <div className="relative">
                <Input
                  id="vat_rate"
                  type="text"
                  value={(() => {
                    // For invoice projects, show the actual effective VAT rate
                    if (project?.is_invoice_project) {
                      // If VAT is explicitly disabled
                      if (project.vat_enabled === false) {
                        return "0";
                      }
                      
                      // If project has line items, get rate from them
                      if (project.line_items && project.line_items.length > 0) {
                        const rates = project.line_items
                          .map(item => item.vat_rate || 0)
                          .filter(rate => rate >= 0); // Include 0 rates
                        
                        if (rates.length === 0) return "0";
                        
                        // Find most common rate
                        const rateCounts = rates.reduce((acc, rate) => {
                          acc[rate] = (acc[rate] || 0) + 1;
                          return acc;
                        }, {});
                        
                        const mostCommonRate = Object.keys(rateCounts)
                          .reduce((a, b) => rateCounts[a] > rateCounts[b] ? a : b);
                        
                        return mostCommonRate;
                      }
                      
                      // Fallback to project VAT rate or 0 if disabled
                      return project.vat_enabled === false ? "0" : (project.vat_rate || "20");
                    }
                    
                    // For non-invoice projects, use form data
                    return formData.vat_rate || 20;
                  })()}
                  className="font-body bg-gray-50"
                  readOnly
                  title="This rate reflects the current invoice VAT settings"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-xs text-gray-500">Auto</span>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-1 font-body">
                {project?.is_invoice_project 
                  ? (project.vat_enabled === false ? "VAT is disabled for this invoice" : "Rate from invoice line items") 
                  : "Default VAT rate for this project"
                }
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="custom_scope" className="font-body">Project Scope (Optional)</Label>
            <Textarea
              id="custom_scope"
              value={formData.custom_scope}
              onChange={(e) => setFormData({ ...formData, custom_scope: e.target.value })}
              placeholder="Describe the project scope and deliverables..."
              rows={4}
              className="font-body"
            />
          </div>

          <div className="flex items-center justify-end pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-black hover:bg-gray-800 text-white font-body"
            >
              {loading ? "Updating..." : "Update Project"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Accounting Software Connection Card Component

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [showCreateQuoteForm, setShowCreateQuoteForm] = useState(false);
  const [showCreateProjectForm, setShowCreateProjectForm] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    fetchQuotes();
    fetchUserSettings();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [filter]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store all projects for filter counts
      setAllProjects(response.data);
      
      // Apply filtering logic - Keep paid projects in active unless manually completed
      let filteredProjects = response.data;
      if (filter === 'active') {
        // Active projects: everything except manually completed and archived
        filteredProjects = response.data.filter(p => p.status !== 'archived' && p.status !== 'completed');
      } else if (filter === 'completed') {
        // Completed projects: only manually marked as completed 
        filteredProjects = response.data.filter(p => p.status === 'completed');
      } else if (filter === 'archived') {
        filteredProjects = response.data.filter(p => p.status === 'archived');
      }
      // 'all' filter shows everything, so no filtering needed
      
      // Sort projects by creation date - newest first
      filteredProjects.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setProjects(filteredProjects);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch projects",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="projects" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="projects" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Projects</h1>
              <p className="text-gray-600 font-body">Manage your project portfolio and quotes.</p>
            </div>
            <div className="flex space-x-3">
              <Button 
                onClick={() => setShowCreateProjectForm(true)}
                className="bg-black hover:bg-gray-800 text-white font-body"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Buttons - Exact Dashboard Design */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 font-heading">
              {filter === 'active' ? 'Active Projects' : 
               filter === 'completed' ? 'Completed Projects' : 
               filter === 'archived' ? 'Archived Projects' : 'All Projects'}
            </h2>
            <div className="flex rounded-md shadow-sm">
              <button
                onClick={() => setFilter('active')}
                className={`px-3 py-1 text-sm font-medium rounded-l-md border font-body ${
                  filter === 'active' 
                    ? 'bg-black text-white border-black' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-3 py-1 text-sm font-medium border-t border-b font-body ${
                  filter === 'completed' 
                    ? 'bg-black text-white border-black' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Completed
              </button>
              <button
                onClick={() => setFilter('archived')}
                className={`px-3 py-1 text-sm font-medium border-t border-b font-body ${
                  filter === 'archived' 
                    ? 'bg-black text-white border-black' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Archived
              </button>
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-sm font-medium rounded-r-md border font-body ${
                  filter === 'all' 
                    ? 'bg-black text-white border-black' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                All
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {projects.length === 0 && quotes.length === 0 ? (
            <Card className="border-2 border-dashed border-gray-300 text-center py-12">
              <CardContent>
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No projects or quotes yet</h3>
                <p className="text-gray-600 font-body mb-6">Create your first quote or project to get started.</p>
                <div className="flex justify-center space-x-4">
                  <Button 
                    onClick={() => setShowCreateQuoteForm(true)}
                    variant="outline"
                    className="font-body"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Create Quote
                  </Button>
                  <Button 
                    onClick={() => navigate('/')}
                    className="bg-black hover:bg-gray-800 text-white font-body"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Project
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Quotes Section - Only show non-converted quotes */}
              {quotes.filter(quote => quote.status !== 'converted').length > 0 && (
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 font-heading">
                    Quotes ({quotes.filter(quote => quote.status !== 'converted').length})
                  </h2>
                  <div className="space-y-4">
                    {quotes.filter(quote => quote.status !== 'converted').map((quote) => (
                      <QuoteCard 
                        key={quote.id} 
                        quote={quote} 
                        onUpdate={() => { fetchQuotes(); fetchProjects(); }}
                        user={user}
                        userSettings={userSettings}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Projects Section */}
              {projects.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 font-heading">
                    Projects ({projects.length})
                  </h2>
                  <div className="space-y-4">
                    {projects.map((project) => (
                      <ProjectCard 
                        key={project.id} 
                        project={project} 
                        onUpdate={fetchProjects}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Create Quote Modal */}
      {showCreateQuoteForm && (
        <CreateQuoteModal
          isOpen={showCreateQuoteForm}
          onClose={() => setShowCreateQuoteForm(false)}
          onSuccess={() => {
            setShowCreateQuoteForm(false);
            fetchQuotes();
          }}
        />
      )}

      {/* Create Project Modal */}
      {showCreateProjectForm && (
        <CreateProjectModal
          isOpen={showCreateProjectForm}
          onClose={() => setShowCreateProjectForm(false)}
          onSuccess={() => {
            setShowCreateProjectForm(false);
            fetchProjects();
          }}
        />
      )}
    </div>
  );
};

// Dedicated Contracts Page Component

export { ProjectCard, EmptyState, ProjectDetail, CreateQuoteFromProjectButton, CreateInvoiceFromProjectButton, UploadInvoiceButton, LinkAccountingSoftwareButton, ClientProgressMirror, ProjectActions, CompleteAndRequestPaymentButton, CreateQuoteFromProjectModal, CreateInvoiceFromProjectModal, LinkAccountingSoftwareModal, ProjectEditForm, ProjectsPage };
